/**
 * Free Shipping Threshold Component
 * Displays a progress bar showing how close the customer is to free shipping.
 * Updates dynamically when cart contents change.
 */
class FreeShippingThreshold extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.messageLeft = this.dataset.messageLeft || '';
    this.messageRight = this.dataset.messageRight || '';
    this.messageComplete = this.dataset.messageComplete || '';

    this.progressBar = this.querySelector('[data-progress-bar]');
    this.thresholdIcon = this.querySelector('[data-threshold-icon]');
    this.thresholdText = this.querySelector('[data-threshold-text]');
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.onCartUpdate(event);
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  /**
   * Handles cart update events and refreshes the progress bar.
   * @param {Object} event - Cart update payload from publish(); cart object is on `cartData`.
   */
  onCartUpdate(event) {
    const cart = event?.cartData ?? event?.cart;
    if (!cart) return;

    const cartTotal = cart.total_price || 0;
    this.updateProgress(cartTotal);
  }

  /**
   * Updates the progress bar and messaging based on cart total.
   * @param {number} cartTotal - The current cart total in cents/pence.
   */
  updateProgress(cartTotal) {
    if (this.threshold <= 0) return;

    const progressPercentage = Math.min((cartTotal / this.threshold) * 100, 100);
    const isQualified = cartTotal >= this.threshold;
    const difference = this.threshold - cartTotal;

    // Update progress bar width
    if (this.progressBar) {
      this.progressBar.style.width = `${progressPercentage}%`;
      this.progressBar.classList.toggle('cart-drawer__threshold-progress--complete', isQualified);
    }

    // Update text message
    if (this.thresholdText) {
      if (isQualified) {
        this.thresholdText.textContent = this.messageComplete;
      } else {
        const formattedDifference = this.formatMoney(difference);
        this.thresholdText.textContent = `${this.messageLeft} ${formattedDifference} ${this.messageRight}`;
      }
    }
  }

  /**
   * Formats a money value (in cents/pence) to a currency string.
   * @param {number} cents - The amount in cents/pence.
   * @returns {string} The formatted currency string.
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Remove trailing zeros after decimal point
    const cleanAmount = parseFloat(amount).toString();
    // Use the shop's currency format - default to £
    return `£${cleanAmount.includes('.') ? amount : cleanAmount}`;
  }
}

customElements.define('free-shipping-threshold', FreeShippingThreshold);
